/**
 * Export Utilities for Dashboard
 *
 * Provides JSON export functionality for dashboards.
 */

import type { Dashboard, DashboardPage } from '../services/dashboard-api.service';

interface ExportData {
  version: string;
  exportedAt: string;
  dashboard: {
    name: string;
    description?: string;
    pages: DashboardPage[];
  };
}

/**
 * Generate JSON export data for a dashboard
 */
export const generateExportJSON = (dashboard: Dashboard): ExportData => {
  return {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    dashboard: {
      name: dashboard.name,
      description: dashboard.description,
      pages: dashboard.pages,
    },
  };
};

/**
 * Trigger download of JSON data
 */
export const downloadJSON = (data: ExportData, filename: string): void => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Generate filename for export
 */
export const generateExportFilename = (dashboardName: string): string => {
  const sanitized = dashboardName.replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = new Date().toISOString().split('T')[0];
  return `${sanitized}_export_${timestamp}.json`;
};

/**
 * Count total widgets across all pages
 */
export const countWidgets = (dashboardOrPages: Dashboard | DashboardPage[]): number => {
  const pages = Array.isArray(dashboardOrPages) ? dashboardOrPages : dashboardOrPages.pages;
  return pages.reduce((total, page) => total + (page.widgets?.length || 0), 0);
};
